import React, { FunctionComponent } from 'react';
import Translate from 'ui/atoms/translate';
import { Modal, makeModalForm, ModalHeader, ModalFooter, ModalContent, ModalFooterButtons } from 'ui/molecules/modal';
import { StateValues } from 'react-use-form-state';
import VoucherSteps, { Step } from './voucher-steps';
import VoucherUsesTotal from './voucher-uses-total';
import VoucherUsesByInvestor from './voucher-uses-investor';
import ProductValidity from './product-validity';
import { AdminVoucherDetail, AdminVoucherStep, DistributionPlatformPermissionEnum } from 'api';
import Hint from 'ui/atoms/hint';
import Section from 'ui/atoms/section';
import { useCreateVoucher } from './hooks';
import { useCurrentUserSelector } from 'core/auth/hooks';

export interface VoucherFields {
  distributionPlatformId: string;
  voucherName: string;
  voucherCode: string;
  numberOfUsesTotal: string;
  numberOfUsesByInvestor: string;
  validityProducts: string;
}

export interface VoucherModalProps {
  voucher?: AdminVoucherDetail;
  isOpen?: boolean;
  isEditMode?: boolean;
  createdSteps?: AdminVoucherStep[];
  onModalClose?: () => void;
  onVoucherChanged: () => void;
}

const VoucherForm = makeModalForm<VoucherFields>();

const VoucherModal: FunctionComponent<VoucherModalProps> = (props) => {
  const { voucher, isOpen, isEditMode = false, createdSteps, onModalClose, onVoucherChanged } = props;

  const {
    isModalOpen,
    error,
    createVoucherValues,
    onSubmit,
    onClose,
    setCreateVoucherValues,
    setNoOfUsesTotalAmount,
    setNoOfUsesByInvestorAmount,
    setSteps,
    setProductsValidity,
  } = useCreateVoucher(isOpen, isEditMode, voucher, onVoucherChanged, onModalClose);

  const { currentUser } = useCurrentUserSelector();

  const distributionPlatformsWithPermission = currentUser?.associatedDistributionPlatforms?.filter(
    (platform) => !!platform.permissions.find((perm) => perm === DistributionPlatformPermissionEnum.MANAGE_VOUCHERS),
  );

  if (!distributionPlatformsWithPermission?.length) return null;

  return (
    <>
      {isModalOpen && (
        <Modal
          size="large"
          onClose={() => {
            if (onModalClose) onModalClose();
            onClose();
          }}
        >
          <VoucherForm
            onChange={(values: StateValues<VoucherFields>) => {
              setCreateVoucherValues({ ...createVoucherValues, ...values });
            }}
            i18nKey="createVoucherForm"
            initial={{
              distributionPlatformId: createVoucherValues.distributionPlatformId,
              voucherName: createVoucherValues.voucherName,
              voucherCode: createVoucherValues.voucherCode,
              numberOfUsesTotal: createVoucherValues.numberOfUsesTotal,
              numberOfUsesByInvestor: createVoucherValues.numberOfUsesByInvestor,
              validityProducts: createVoucherValues.validityProducts,
            }}
          >
            <ModalHeader>
              {isEditMode ? (
                <Translate name="dashboardIssuerVoucherDetails.editVoucher" />
              ) : (
                <Translate name="dashboardIssuerVouchers.create" />
              )}
            </ModalHeader>
            <ModalContent>
              <Section spacing="regular">
                {!!distributionPlatformsWithPermission?.length && (
                  <VoucherForm.Group name="distributionPlatformId" required={true}>
                    <VoucherForm.Select
                      options={distributionPlatformsWithPermission.map(({ id, displayName }) => ({
                        value: id,
                        label: displayName,
                      }))}
                    ></VoucherForm.Select>
                  </VoucherForm.Group>
                )}
                <VoucherForm.Group name="voucherName" required={true}>
                  <VoucherForm.Input autoFocus={true} />
                </VoucherForm.Group>

                <VoucherForm.Group name="voucherCode" required={true}>
                  <VoucherForm.Input />
                </VoucherForm.Group>
              </Section>

              <Section spacing="regular">
                <VoucherSteps onChangeSteps={(steps: Step[]) => setSteps(steps)} createdSteps={createdSteps} />
              </Section>

              <Section spacing="regular">
                <ProductValidity
                  Form={VoucherForm}
                  values={createVoucherValues}
                  selectedValidityProducts={voucher?.productNames}
                  selectedDistributionPlatform={createVoucherValues.distributionPlatformId}
                  onChangeValidity={(products) => setProductsValidity(products)}
                />
              </Section>

              <Section spacing="regular">
                <VoucherUsesTotal
                  Form={VoucherForm}
                  values={createVoucherValues}
                  numberOfUsesTotalAmount={voucher?.numberOfUses}
                  onChangeUsesTotal={(noOfUses, amount) => {
                    setCreateVoucherValues({
                      ...createVoucherValues,
                      numberOfUsesTotal: noOfUses,
                    });
                    setNoOfUsesTotalAmount(amount);
                  }}
                />
              </Section>

              <Section spacing="regular">
                <VoucherUsesByInvestor
                  Form={VoucherForm}
                  values={createVoucherValues}
                  numberOfUsesByInvestorAmount={voucher?.numberOfUsesInvestor}
                  onChangeUsesByInvestor={(noOfUses, amount) => {
                    setCreateVoucherValues({
                      ...createVoucherValues,
                      numberOfUsesByInvestor: noOfUses,
                    });
                    setNoOfUsesByInvestorAmount(amount);
                  }}
                />
              </Section>

              {error && (
                <Section spacing="regular">
                  <Hint variant="danger">
                    <Translate name={error} />
                  </Hint>
                </Section>
              )}
            </ModalContent>
            <ModalFooter>
              <ModalFooterButtons
                actionButtons={[
                  {
                    name: 'cancel',
                    content: <Translate name="common.cancel" />,
                    size: 'large',
                    onClick: onClose,
                  },
                  {
                    name: 'add',
                    content: isEditMode ? <Translate name="common.saveChanges" /> : <Translate name="common.add" />,
                    variant: 'primary',
                    type: 'submit',
                    size: 'large',
                    onClick: onSubmit,
                  },
                ]}
              />
            </ModalFooter>
          </VoucherForm>
        </Modal>
      )}
    </>
  );
};

export default VoucherModal;
